import { assign, createMachine } from "xstate";
import { resetPassword } from "@nju/data-access-portal-user";
import { isValidEmail } from "@nju/utils";

interface IContext {
  email: string | undefined;
  emailError?: string;
  formError?: string;
}

type IEvent =
  | {
      type: "SET_EMAIL";
      name: "email";
      value: string;
    }
  | { type: "SUBMIT" }
  | { type: "RESET_SUCCESSFUL" }
  | { type: "EMAIL_ERROR" }
  | { type: "RESET_ERROR" };

export const resetPasswordMachine = createMachine<IContext, IEvent>(
  {
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    context: {
      email: "",
    },
    initial: "ready",
    states: {
      ready: {
        on: {
          SET_EMAIL: {
            actions: "setValue",
          },
          SUBMIT: {
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validate",
        always: [
          {
            cond: "hasNoInputErrors",
            target: "send",
          },
          {
            target: "ready",
          },
        ],
      },
      send: {
        invoke: {
          src: "sendResetEmail",
        },
        on: {
          RESET_SUCCESSFUL: {
            actions: "onSubmit",
          },
          EMAIL_ERROR: {
            actions: "setFormError",
            target: "ready",
          },
          RESET_ERROR: {
            actions: "setResetError",
            target: "ready",
          },
        },
      },
    },
  },
  {
    actions: {
      setValue: assign<IContext, IEvent>((context, event: IEvent) => {
        if (event.type === "SET_EMAIL") {
          return {
            [event.name]: event.value,
          };
        }
        return context;
      }),
      validate: assign<IContext, IEvent>((context) => {
        let emailError: string | undefined;

        if (
          context.email &&
          (!context.email.trim() || !isValidEmail(context.email))
        ) {
          emailError = "Wpisz poprawny adres e-mail.";
        }

        return {
          ...context,
          formError: undefined,
          emailError,
        };
      }),
      setFormError: assign<IContext, IEvent>(() => {
        return {
          formError: "Wprowadź poprawne dane.",
        };
      }),
      setResetError: assign<IContext, IEvent>(() => {
        return {
          formError: "Nie można zresetować hasła.",
        };
      }),
    },
    services: {
      sendResetEmail: ({ email }) => {
        return async (callback) => {
          try {
            if (email) {
              (await resetPassword({ email })).unwrap();
              return callback({ type: "RESET_SUCCESSFUL" });
            }

            return callback({ type: "EMAIL_ERROR" });
          } catch {
            return callback({ type: "RESET_ERROR" });
          }
        };
      },
    },
    guards: {
      hasNoInputErrors: (context) => {
        return context.emailError === undefined;
      },
    },
  },
);
